import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiCheck, FiCopy, FiCloud, FiMonitor, FiCommand } from "react-icons/fi";
import { linkiZasobu, skopiujDoSchowka, adresSerweraPlikow } from "./regulaNazwyDysku";
import s from "./PrzyciskiKopiowania.module.css";

/* ════════════════════════════════════════════════════════════════════════════════════════
   TRZY OSOBNE PRZYCISKI KOPIOWANIA LINKU — Windows, macOS, SharePoint.

   [polecenie właściciela 2026-09-12]
   „dodaj opcję kopiowania linka dla macbooka oraz dla windows, oraz co ważne, dodaj opcje
    kopiowania linka do sharepoint, jako oddzielne linki w sekcji konfiguracji tak by dało się
    to łatwo przeklikać (to samo w sekcji - moj-dzial)"

   ── DLACZEGO TRZY PRZYCISKI, A NIE JEDEN Z LISTĄ ─────────────────────────────────────────
   „tak by dało się to łatwo przeklikać" to wymaganie o LICZBIE KLIKNIĘĆ. Jeden przycisk
   z rozwijaną listą to trzy kliknięcia na jedną ścieżkę (otwórz → wybierz → kopiuj) i decyzja
   do podjęcia za każdym razem. Trzy przyciski to jedno kliknięcie i zero decyzji.

   ── DLACZEGO WSPÓLNY KOMPONENT, A NIE KOD W DWÓCH EKRANACH ───────────────────────────────
   Właściciel wymaga tego samego w /admin/drives i w /moj-dzial. Dwie kopie rozjadą się przy
   pierwszej poprawce — a rozjazd objawi się tym, że dyrektor skopiuje INNĄ ścieżkę niż
   administrator i jedna z nich nie zadziała.

   ⚠ POTWIERDZENIE MUSI MÓWIĆ PRAWDĘ. `navigator.clipboard` nie istnieje bez HTTPS i rzuca pod
   Safari — przycisk, który po każdym kliknięciu pokazuje „Skopiowano", kłamie w dokładnie tych
   przypadkach, w których człowiek najbardziej potrzebuje wiedzieć, że schowek jest pusty.
   Dlatego stan bierzemy z WYNIKU `skopiujDoSchowka`, a nie z faktu kliknięcia.
   ════════════════════════════════════════════════════════════════════════════════════════ */

/** Jak długo trzymamy potwierdzenie „Skopiowano”. */
const CZAS_POTWIERDZENIA_MS = 2200;

const WARIANTY = [
    {
        klucz: "windows",
        etykieta: "Windows",
        ikona: FiMonitor,
        tytul: (link) => `Kopiuj ścieżkę dla Windows: ${link}`,
        brak: "Ten zasób nie ma adresu sieciowego.",
    },
    {
        klucz: "mac",
        etykieta: "macOS",
        ikona: FiCommand,
        tytul: (link) => `Kopiuj adres dla macOS (Finder → Połącz z serwerem): ${link}`,
        brak: "Ten zasób nie ma adresu sieciowego.",
    },
    {
        klucz: "sharepoint",
        etykieta: "SharePoint",
        ikona: FiCloud,
        tytul: (link) => `Kopiuj adres witryny SharePoint: ${link}`,
        brak: "Ten zasób nie jest witryną SharePoint.",
    },
];

/**
 * @param {object}   props
 * @param {object}   props.dysk       wiersz zasobu (`AssignableDriveDto`)
 * @param {boolean}  [props.kompakt]  wariant do tabeli (same ikony, bez napisów)
 * @param {Function} [props.onKomunikat] (tekst, rodzaj) — toast strony; bez niego potwierdzenie
 *                                        widać wyłącznie na przycisku
 */
const PrzyciskiKopiowania = ({ dysk, kompakt = false, onKomunikat }) => {
    const linki = linkiZasobu(dysk);
    const [skopiowany, setSkopiowany] = useState(null);
    const licznikRef = useRef(null);

    // Odmierzanie trzeba skasować przy odmontowaniu — inaczej `setState` po zniknięciu wiersza
    // (np. po odświeżeniu listy z SignalR) sypie ostrzeżeniem i zostawia wyciek.
    useEffect(() => () => {
        if (licznikRef.current) clearTimeout(licznikRef.current);
    }, []);

    const kopiuj = useCallback(async (wariant, link) => {
        const ok = await skopiujDoSchowka(link);

        if (licznikRef.current) clearTimeout(licznikRef.current);

        if (ok) {
            setSkopiowany(wariant.klucz);
            licznikRef.current = setTimeout(() => setSkopiowany(null), CZAS_POTWIERDZENIA_MS);
            onKomunikat?.(`Skopiowano link ${wariant.etykieta}: ${link}`, "success");
        } else {
            setSkopiowany(null);
            onKomunikat?.(
                "Przeglądarka nie pozwoliła skopiować do schowka. Zaznacz adres i skopiuj ręcznie: " + link,
                "error"
            );
        }
    }, [onKomunikat]);

    return (
        <div
            className={`${s.pasek} ${kompakt ? s.paskKompakt : ""}`}
            role="group"
            aria-label="Kopiowanie linku do zasobu"
            // Wiersz tabeli otwiera okno dostępów; kliknięcie w przycisk kopiowania NIE MOŻE tego robić.
            onClick={(e) => e.stopPropagation()}
        >
            {WARIANTY.map((wariant) => {
                const link = linki[wariant.klucz] || "";
                const dostepny = Boolean(link);
                /* [zgłoszenie właściciela 2026-09-16] Gdy zasób MA udział, a linku nie ma, brakuje
                   jednego członu: adresu serwera plików (ustawienie instalacji). Zdanie „ten zasób
                   nie ma adresu sieciowego" byłoby wtedy nieprawdą i wysyłało człowieka do edycji
                   zasobu zamiast do ustawienia — a takich zasobów jest na produkcji 394. */
                const brakSerwera = !dostepny
                    && wariant.klucz !== "sharepoint"
                    && Boolean(String(dysk?.masterDiskAddress || "").trim())
                    && !adresSerweraPlikow();
                const powodBraku = brakSerwera
                    ? "Nie ustawiono adresu serwera plików — ustaw go na /admin/drives, wtedy ścieżka będzie pełna."
                    : wariant.brak;
                const czySkopiowany = skopiowany === wariant.klucz;
                const Ikona = czySkopiowany ? FiCheck : wariant.ikona;

                return (
                    <button
                        key={wariant.klucz}
                        type="button"
                        className={`${s.przycisk} ${czySkopiowany ? s.przyciskOk : ""}`}
                        disabled={!dostepny}
                        onClick={() => kopiuj(wariant, link)}
                        title={dostepny ? wariant.tytul(link) : powodBraku}
                        aria-label={dostepny
                            ? `Kopiuj link ${wariant.etykieta}`
                            : `${wariant.etykieta}: ${powodBraku}`}
                        data-testid={`kopiuj-${wariant.klucz}`}
                        data-skopiowano={czySkopiowany ? "tak" : "nie"}
                    >
                        <Ikona size={13} aria-hidden="true" />
                        {!kompakt && <span>{czySkopiowany ? "Skopiowano" : wariant.etykieta}</span>}
                        {kompakt && <span className={s.tylkoDlaCzytnika}>{wariant.etykieta}</span>}
                    </button>
                );
            })}
            {!kompakt && (
                <FiCopy size={12} className={s.ikonaPaska} aria-hidden="true" title="Trzy osobne linki tego samego zasobu" />
            )}
        </div>
    );
};

export default PrzyciskiKopiowania;
